import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { trackOnboardingStepViewed } from '../lib/posthogEvents'

import { useRedirectToStep1Effect, useAutofillFixEffect, useAutocompleteDebugEffect, useStepFromPathEffect, useLoadOnboardingDataEffect } from './Onboarding/effects'
import { handleFileChange as handleFileChangeHandler, uploadResume, uploadAiMemory, convertResumeToMarkdown } from './Onboarding/fileHandlers'
import { handleSubmit, handleSkip } from './Onboarding/formHandlers'
import { handleInputChange, handleNext, handleBack } from './Onboarding/handlers'
import { getStepFromPath } from './Onboarding/helpers'
import { NavigationButtons } from './Onboarding/NavigationButtons'
import { ProgressIndicator } from './Onboarding/ProgressIndicator'
import { Step1PersonalInfo } from './Onboarding/Step1PersonalInfo'
import { Step2UploadResume } from './Onboarding/Step2UploadResume'
import { Step3AIMemory } from './Onboarding/Step3AIMemory'
import { OnboardingData, AIProvider } from './Onboarding/types'

function Onboarding() {
  const navigate = useNavigate()
  const location = useLocation()
  
  
  // Total number of steps
  const totalSteps = 3
  
  const [currentStep, setCurrentStep] = useState(() => getStepFromPath(location.pathname))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [data, setData] = useState<OnboardingData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    resumeFile: null,
  })

  const [resumeFilePath, setResumeFilePath] = useState<string | null>(null)
  const [resumeMdPath, setResumeMdPath] = useState<string | null>(null)
  const [resumeUploading, setResumeUploading] = useState(false)
  const [resumeUploaded, setResumeUploaded] = useState(false)

  // AI Memory step state
  const [aiProvider, setAiProvider] = useState<AIProvider>(null)
  const [aiMemoryFile, setAiMemoryFile] = useState<File | null>(null)
  const [aiMemoryFilePath, setAiMemoryFilePath] = useState<string | null>(null)
  const [aiMemoryUploading, setAiMemoryUploading] = useState(false)
  const [aiMemoryUploaded, setAiMemoryUploaded] = useState(false)

  // Track if data has been loaded to prevent re-fetching on step navigation
  const [dataLoaded, setDataLoaded] = useState(false)

  // Use effects from effects module
  useRedirectToStep1Effect(location, navigate)
  useAutofillFixEffect(data, setData)
  useAutocompleteDebugEffect(currentStep)
  useStepFromPathEffect(location, setCurrentStep)
  useLoadOnboardingDataEffect(
    dataLoaded,
    setDataLoaded,
    setData,
    setResumeFilePath,
    setResumeUploaded,
    setResumeMdPath,
    setAiProvider,
    setAiMemoryFilePath,
    setAiMemoryUploaded,
    setLoading,
    navigate,
    data
  )

  useEffect(() => {
    trackOnboardingStepViewed(currentStep)
  }, [currentStep])

  const handleInputChangeWrapper = (field: keyof OnboardingData, value: string | number | null) => {
    handleInputChange(field, value, setData, setError)
  }

  // Wrapper for file handlers
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      await handleFileChangeHandler(
        file,
        setError,
        setResumeUploaded,
        handleInputChangeWrapper,
        uploadResumeWrapper
      )
    }
  }

  const uploadResumeWrapper = async (file: File) => {
    await uploadResume(
      file,
      setResumeUploading,
      setError,
      setResumeFilePath,
      setResumeUploaded,
      setResumeMdPath,
      convertResumeToMarkdownWrapper
    )
  }

  const convertResumeToMarkdownWrapper = async (file: File, originalFilePath: string) => {
    await convertResumeToMarkdown(
      file,
      originalFilePath,
      setResumeConverting,
      setError,
      setResumeMdPath
    )
  }

  const uploadAiMemoryWrapper = async (file: File) => {
    await uploadAiMemory(
      file,
      setAiMemoryUploading,
      setError,
      setAiMemoryFilePath,
      setAiMemoryUploaded
    )
  }


  // Wrapper functions for handlers
  const handleNextWrapper = () => {
    handleNext(
      currentStep,
      totalSteps,
      data,
      setError,
      navigate,
      handleSubmitWrapper
    )
  }

  const handleBackWrapper = () => {
    handleBack(currentStep, navigate, setError)
  }

  const handleSubmitWrapper = async (e: React.FormEvent) => {
    await handleSubmit(
      e,
      data,
      resumeFilePath,
      resumeMdPath,
      aiProvider,
      aiMemoryFilePath,
      setSaving,
      setError,
      navigate
    )
  }

  const handleSkipWrapper = async () => {
    await handleSkip(
      data,
      resumeFilePath,
      resumeMdPath,
      aiProvider,
      aiMemoryFilePath,
      navigate
    )
  }

  // Only show loading screen on initial load (when we have no data at all)
  // Don't show loading when navigating between steps or when data is already loaded
  if (loading && !data.email && !data.firstName && !data.lastName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-muted">
        <div className="text-text-secondary">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface-subtle py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-surface-default shadow-sm rounded-lg p-8 sm:p-6">
          <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />

          <h2 className="text-h2 text-text-primary mb-6">
            {currentStep === 1 && 'Personal Information'}
            {currentStep === 2 && 'Upload Resume'}
            {currentStep === 3 && 'AI Memory Import'}
          </h2>

          {error && (
            <div className="mb-4 bg-danger-subtle border border-danger/20 text-danger-strong px-4 py-3 rounded">
              <div className="whitespace-pre-line text-sm">{error}</div>
            </div>
          )}

          <form 
            autoComplete="on"
            method="post"
            onSubmit={(e) => { e.preventDefault(); handleNextWrapper(); }}
          >
            {/* Hidden field for full name - helps Chrome understand the structure and provides fallback */}
            <input
              type="text"
              name="name"
              autoComplete="name"
              defaultValue={`${data.firstName} ${data.lastName}`.trim()}
              onInput={(e) => {
                // When Chrome autofills the full name, split it into first and last
                const fullName = (e.target as HTMLInputElement).value.trim()
                if (fullName && fullName.includes(' ')) {
                  const parts = fullName.split(' ')
                  const firstName = parts[0]
                  const lastName = parts.slice(1).join(' ')
                  if (firstName && lastName) {
                    setData(prev => ({
                      ...prev,
                      firstName: firstName,
                      lastName: lastName,
                    }))
                  }
                }
              }}
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
            />
            
            <Step1PersonalInfo
              data={data}
              currentStep={currentStep}
              handleInputChangeWrapper={handleInputChangeWrapper}
              setData={setData}
            />

            {currentStep === 2 && (
              <Step2UploadResume
                data={data}
                resumeUploaded={resumeUploaded}
                resumeUploading={resumeUploading}
                handleFileChange={handleFileChange}
                setResumeUploaded={setResumeUploaded}
                setResumeFilePath={setResumeFilePath}
                setResumeMdPath={setResumeMdPath}
                handleInputChangeWrapper={handleInputChangeWrapper}
              />
            )}

            {currentStep === 3 && (
              <Step3AIMemory
                aiProvider={aiProvider}
                aiMemoryFile={aiMemoryFile}
                aiMemoryUploaded={aiMemoryUploaded}
                aiMemoryUploading={aiMemoryUploading}
                setAiProvider={setAiProvider}
                setAiMemoryFile={setAiMemoryFile}
                setAiMemoryFilePath={setAiMemoryFilePath}
                setAiMemoryUploaded={setAiMemoryUploaded}
                setError={setError}
                uploadAiMemory={uploadAiMemoryWrapper}
              />
            )}


            <NavigationButtons
              currentStep={currentStep}
              totalSteps={totalSteps}
              resumeUploading={resumeUploading}
              saving={saving}
              aiMemoryUploading={aiMemoryUploading}
              handleBackWrapper={handleBackWrapper}
              handleSkip={handleSkipWrapper}
              handleNext={handleNextWrapper}
            />
          </form>
        </div>
      </div>
    </div>
  )
}

export default Onboarding
